import { Feedback, Input, Field } from '@availity/form';
import { Formik } from 'formik';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Label } from 'reactstrap';
import { FileSelector } from '../../../../components/FileSelector/FileSelector';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../../slices/marketplace/timeline/models/document-details.model';
import {
    ActionDocumentType,
    EngagementDocumentTypeOptionsMapping,
} from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import {
    EngagementDocumentSource,
    EngagementDocumentSourceOptionsMapping,
} from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    defaultAdditionalOldRefIdsField,
    isValidAdditionalOldRefIdsField,
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { useStateSelector } from '../../../../store/selectors';
import * as yup from 'yup';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import './SellerEngagementDocumentForm.scss';
import { FileSelectorService } from '../../../../components/FileSelector/FileSelector.service';
import Helpers from '../../../../utils/helper';
import createNotification from '../../../../utils/createNotification';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import {
    DocumentDetailsContentType,
    DocumentDetailsContentTypeOptions,
} from '../../../../slices/marketplace/timeline/models/enums/document-details-content-type';
import { TemplateDropdownOption } from '../../../../models/DataRequestHub/TemplateDropdownOption';
import { DataFormType } from '../../../../models/DataRequestHub/DataFormTypeEnum';
import { DataForm } from '../../../../models/DataRequestHub/DataForm';
import { LinkedDataRequestProject } from '../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import {
    DocumentDetailsContentSubtype,
    DocumentDetailsContentSubtypeOptions,
    DocumentDetailsContentSubtypeEnumToStringMapping as subTypeEnumToStringMap,
} from '../../../../slices/marketplace/timeline/models/enums/document-details-content-subtype';
import { generateGuid } from '../../../../utils/typedHelper';

interface SellerEngagementDocumentFormProps {
    onSubmit(
        action: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        questionnaire?: LinkedDataRequestProject
    ): void;
    disabled: boolean;
    downloadFile(documentId?: string, documentDetails?: DocumentDetails): void;
    masterDocumentDetails?: DocumentDetails;
    documentsSections: DocumentSection[];
    timelineEvents: TimelineEvent[];
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    setIsFileUploading(isUploading: boolean): void;
    setIsDocumentsHistoryPopupVisible(
        isVisible: boolean,
        documentId?: string,
        subtype?: DocumentDetailsContentSubtype
    ): void;
    setDocumentSectionList(sections: DocumentSection[]): void;
    documentDetailsList: DocumentDetails[];
    dataRequestProjects: LinkedDataRequestProject[];
    isTemplate: boolean;
}
export const SellerEngagementDocumentForm = forwardRef(
    (props: SellerEngagementDocumentFormProps, ref) => {
        const formRef = useRef<any>();
        const axios = useStateSelector((state) => state.core.axios);

        const documentTemplates = useStateSelector(
            (state) => state.timelines.documentTemplatesTable.templates
        );

        const [selectedMasterFile, setSelectedMasterFile] =
            useState<File | null>(null);
        const [selectedAdditionalFiles, setSelectedAdditionalFiles] = useState<
            {
                subType: DocumentDetailsContentSubtype;
                isUpdated: boolean;
                file: File | null;
            }[]
        >(
            DocumentDetailsContentSubtypeOptions.filter(
                (o) => o.id != DocumentDetailsContentSubtype.Blinded
            ).map((o) => {
                return {
                    subType: o.id,
                    isUpdated: false,
                    file: null as File,
                };
            })
        );

        const getDefaultAdditionalOldDocIds = () =>
            DocumentDetailsContentSubtypeOptions.filter(
                (o) => o.id != DocumentDetailsContentSubtype.Blinded
            ).map((o) => {
                return {
                    subType: o.id,
                    id: null as string,
                };
            });

        const [oldMasterDocumentId, setOldMasterDocumentId] =
            useState<string>(null);
        const [oldAdditionalDocIds, setOldAdditionalDocIds] = useState<
            {
                subType: DocumentDetailsContentSubtype;
                id: string | null;
            }[]
        >(getDefaultAdditionalOldDocIds());

        const [isMasterFileUpdated, setIsMasterFileUpdated] = useState(false);

        const [projectQuestionnaires, setProjectQuestionnaires] = useState<
            TemplateDropdownOption[]
        >([]);
        const [projectsAvailableToSelect, setProjectsAvailableToSelect] =
            useState<LinkedDataRequestProject[]>([]);

        const [masterDocumentDetails, setMasterDocumentDetails] = useState(
            props.masterDocumentDetails ??
                createDefaultDocumentDetails(
                    true,
                    null,
                    null,
                    null,
                    null,
                    TimelineParty.Seller
                )
        );
        const [additionalDocumentDetails, setAdditionalDocumentDetails] =
            useState<DocumentDetails[]>([]);

        const [fileSelectorService] = useState(new FileSelectorService(axios));

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        useEffect(() => {
            if (!!props.masterDocumentDetails?.linkedProjectId) {
                fetchTemplateOptions(
                    props.masterDocumentDetails.linkedProjectId
                );
            }
            initializeProjectsAvailableToSelect();
            initializeAdditionalDocuments();
        }, []);

        const initializeAdditionalDocuments = (
            contentType?: DocumentDetailsContentType
        ) => {
            if (
                props.linkedAction &&
                props.masterDocumentDetails?.additionalVersionIds?.length > 0
            ) {
                setAdditionalDocumentDetails(
                    props.documentDetailsList.filter((d) =>
                        props.masterDocumentDetails.additionalVersionIds.some(
                            (id) => d.id == id
                        )
                    )
                );
            } else {
                const details = DocumentDetailsContentSubtypeOptions.filter(
                    (o) => o.id != DocumentDetailsContentSubtype.Blinded
                ).map((o) => {
                    return {
                        ...createDefaultDocumentDetails(
                            true,
                            masterDocumentDetails.id,
                            ActionDocumentType.DocumentWithFeedback,
                            o.id,
                            EngagementDocumentSource.AdminUpload,
                            TimelineParty.Seller
                        ),
                        contentType:
                            contentType ?? DocumentDetailsContentType.General,
                    };
                });

                setMasterDocumentDetails({
                    ...masterDocumentDetails,
                    additionalVersionIds: details.map((d) => d.id),
                });

                setAdditionalDocumentDetails(details);
            }
        };

        const uploadFile = async (
            updateFlag: boolean = true,
            subType?: DocumentDetailsContentSubtype
        ) => {
            if (updateFlag) props.setIsFileUploading(true);
            let fileId: number;
            if (!subType) {
                fileId = await fileSelectorService.uploadFile(
                    selectedMasterFile
                );
            } else {
                const addFileInfo = selectedAdditionalFiles.find(
                    (af) => af.subType == subType
                );
                fileId = await fileSelectorService.uploadFile(
                    addFileInfo?.file
                );
            }
            if (updateFlag) props.setIsFileUploading(false);

            if (!subType) {
                return {
                    ...masterDocumentDetails,
                    isUnregisteredFile: true,
                    temporaryFileId: fileId,
                    fileName: selectedMasterFile?.name,
                } as DocumentDetails;
            } else {
                const addDocDetails = additionalDocumentDetails.find(
                    (dd) => dd.contentSubtype == subType
                );
                const addFileInfo = selectedAdditionalFiles.find(
                    (af) => af.subType == subType
                );
                return {
                    ...addDocDetails,
                    isUnregisteredFile: true,
                    temporaryFileId: fileId,
                    fileName: addFileInfo?.file?.name,
                } as DocumentDetails;
            }
        };

        const changeFileHandler = (
            file: File,
            masterDocumentId?: string,
            subType?: DocumentDetailsContentSubtype
        ) => {
            if (file) {
                const isMasterFile =
                    !subType &&
                    (!masterDocumentId ||
                        masterDocumentId == masterDocumentDetails.id);

                if (isMasterFile) {
                    setIsMasterFileUpdated(true);
                    setSelectedMasterFile(file);
                    formRef.current.setFieldValue(
                        'masterFileName',
                        file.name,
                        true
                    );
                    formRef.current.setFieldValue(
                        'masterSourceFile',
                        file.name,
                        true
                    );
                } else {
                    formRef.current.setFieldValue(
                        `${
                            DocumentDetailsContentSubtypeOptions.find(
                                (o) => o.id == subType
                            ).fieldName
                        }SourceFile`,
                        file.name,
                        true
                    );
                    setSelectedAdditionalFiles(
                        selectedAdditionalFiles.map((f) =>
                            f.subType == subType
                                ? { ...f, file: file, isUpdated: true }
                                : f
                        )
                    );
                }

                const id = generateGuid();

                const newDocument: DocumentDetails = {
                    ...masterDocumentDetails,
                    id: id,
                    masterDocumentId: isMasterFile
                        ? id
                        : masterDocumentId ?? id,
                    name: file.name,
                    isUnregisteredFile: true,
                    localFile: file,
                    source: EngagementDocumentSource.AdminUpload,
                    contentSubtype: subType,
                };

                if (isMasterFile) {
                    setOldMasterDocumentId(masterDocumentDetails.id);
                    setMasterDocumentDetails(newDocument);
                    setAdditionalDocumentDetails([
                        ...additionalDocumentDetails.map((d) => {
                            return { ...d, masterDocumentId: newDocument.id };
                        }),
                    ]);
                } else {
                    const oldId = additionalDocumentDetails.find(
                        (d) => d.contentSubtype == subType
                    )?.id;

                    setAdditionalDocumentDetails([
                        ...additionalDocumentDetails.map((d) =>
                            d.contentSubtype == subType ? newDocument : d
                        ),
                    ]);

                    const newAdditionalVersionIds = [
                        ...masterDocumentDetails.additionalVersionIds.filter(
                            (id) => id != oldId
                        ),
                    ];
                    newAdditionalVersionIds.push(id);
                    setMasterDocumentDetails({
                        ...masterDocumentDetails,
                        additionalVersionIds: newAdditionalVersionIds,
                    });

                    setOldAdditionalDocIds(
                        oldAdditionalDocIds.map((o) =>
                            o.subType == subType ? { ...o, id: oldId } : o
                        )
                    );
                }
            }
        };

        const initializeProjectsAvailableToSelect = () => {
            const indexOfCurrentEvent = props.timelineEvents.findIndex(
                (e) => e.id === props.currentEvent.id
            );
            const eventsAfterCurrentEvent = props.timelineEvents
                .filter((_e, index) => index > indexOfCurrentEvent)
                .map((e) => e.id);
            const availableProjects = props.dataRequestProjects.filter(
                (x) =>
                    !props.timelineEvents.some(
                        (e) =>
                            e.linkedAction?.actionType ===
                                TimelineActionType.SellerDataAndDocumentRequest &&
                            e.linkedAction.referenceItemId === x.id &&
                            eventsAfterCurrentEvent.includes(e.id)
                    ) &&
                    (props.isTemplate || x.dataRequestProjectId)
            );

            setProjectsAvailableToSelect(availableProjects);
        };

        const fetchTemplateOptions = (projectId: string) => {
            if (projectId === '0') {
                setProjectQuestionnaires([]);
                return;
            }

            const project = props.dataRequestProjects.find(
                (x) => x.id === projectId
            );

            if (props.isTemplate) {
                axios
                    .get('/api/dataRequestTemplateProject/dataForms')
                    .then((response) => {
                        if (response.status === 200) {
                            const filteredQuestionnaireTemplateOptions =
                                getFilteredDataFormTemplateOptions(
                                    response.data,
                                    DataFormType.Questionnaire
                                );

                            if (project?.questionnaireTemplateId) {
                                setProjectQuestionnaires(
                                    filteredQuestionnaireTemplateOptions.filter(
                                        (q) =>
                                            q.id ===
                                            project.questionnaireTemplateId
                                    )
                                );
                            } else {
                                setProjectQuestionnaires([]);
                            }
                        } else {
                            createNotification(
                                'An error occured while fetching templates',
                                'error'
                            );
                        }
                    });
            } else {
                axios
                    .get(
                        `/api/DataRequestProjects/${project.dataRequestProjectId}`
                    )
                    .then((response) => {
                        if (response?.status === 200) {
                            const newForms = response.data
                                .dataForms as DataForm[];
                            const questionnaires = newForms.filter(
                                (f) => f.formType === DataFormType.Questionnaire
                            );
                            const filteredQuestionnaireTemplateOptions =
                                getFilteredDataFormTemplateOptions(
                                    questionnaires,
                                    DataFormType.Questionnaire
                                );
                            setProjectQuestionnaires(
                                filteredQuestionnaireTemplateOptions
                            );
                        } else {
                            createNotification(
                                'An error occured while fetching templates',
                                'error'
                            );
                        }
                    });
            }
        };

        const getFilteredDataFormTemplateOptions = (
            templateForms: DataForm[],
            targetFormType: DataFormType
        ): TemplateDropdownOption[] => {
            const result = templateForms
                .map((obj: DataForm) => ({
                    id: obj.id,
                    name: obj.customName ?? obj.originalTemplateName,
                    formType: obj.formType,
                }))
                .filter((x) => x.formType === targetFormType)
                .sort((a, b) => Helpers.stringSortFunction(a.name, b.name));
            return result;
        };

        const getDefaultAdditionalDocDetails = (
            contentType?: DocumentDetailsContentType
        ) => {
            return DocumentDetailsContentSubtypeOptions.filter(
                (o) => o.id != DocumentDetailsContentSubtype.Blinded
            ).map((o) => {
                return {
                    ...createDefaultDocumentDetails(
                        true,
                        masterDocumentDetails.id,
                        ActionDocumentType.DocumentWithFeedback,
                        o.id,
                        EngagementDocumentSource.AdminUpload,
                        TimelineParty.Seller
                    ),
                    contentType:
                        contentType ?? (-1 as DocumentDetailsContentType),
                };
            });
        };

        const switchAdditionalDetailsContentType = (
            newContentType: DocumentDetailsContentType
        ) => {
            return additionalDocumentDetails.length > 0
                ? [
                      ...additionalDocumentDetails.map((d) => {
                          return { ...d, contentType: newContentType };
                      }),
                  ]
                : getDefaultAdditionalDocDetails(newContentType);
        };

        const getOldReferenceIdsList = (
            subType?: DocumentDetailsContentSubtype
        ) => {
            if (!subType || subType == DocumentDetailsContentSubtype.Blinded) {
                return props.linkedAction?.oldReferenceItemIds;
            } else {
                const subtypeOldRefIds = !props.linkedAction
                    ?.oldReferenceItemAdditionalVersionIds
                    ? []
                    : props.linkedAction?.oldReferenceItemAdditionalVersionIds[
                          subTypeEnumToStringMap[subType]
                      ];
                return subtypeOldRefIds;
            }
        };

        const getAdditionalFileFields = (
            actionType: ActionDocumentType,
            option: (typeof DocumentDetailsContentSubtypeOptions)[0],
            index: number
        ) => {
            if (option.id == DocumentDetailsContentSubtype.Blinded)
                return <></>;

            const addDocDetails = additionalDocumentDetails.find(
                (d) => d.contentSubtype == option.id
            );

            return (
                <>
                    <FileSelector
                        extension={
                            EngagementDocumentTypeOptionsMapping.find(
                                (o) => Number(actionType) === o.value
                            ).fileExtensions
                        }
                        disabled={props.disabled}
                        file={
                            selectedAdditionalFiles.find(
                                (f) => f.subType == option.id
                            )?.file ?? null
                        }
                        setFile={(file: File) => {
                            changeFileHandler(
                                file,
                                masterDocumentDetails.id,
                                option.id
                            );
                        }}
                        inputFieldName={`${option.fieldName}SourceFile`}
                        fileName={addDocDetails?.name}
                        downloadFile={() =>
                            props.downloadFile(undefined, addDocDetails)
                        }
                        setIsDocumentsHistoryPopupVisible={(isVisible) =>
                            props.setIsDocumentsHistoryPopupVisible(
                                isVisible,
                                addDocDetails.id,
                                option.id
                            )
                        }
                        oldDocumentDetailsList={getOldReferenceIdsList(
                            option.id
                        )}
                    />
                    <Feedback name={`${option.fieldName}FileName`} />

                    <Field
                        className={`field ${
                            index ==
                            DocumentDetailsContentSubtypeOptions.length - 1
                                ? ''
                                : 'small-margin'
                        }`}
                        name={`${option.fieldName}DocumentName`}
                        label={`${option.label} Document Name`}
                        disabled={props.disabled}
                    />
                </>
            );
        };

        const getInitialDocumentPropValue = (
            masterFileId: string,
            contentType: DocumentDetailsContentType,
            subtype: DocumentDetailsContentSubtype,
            prop: keyof DocumentDetails
        ) => {
            if (contentType == DocumentDetailsContentType.General)
                return !props.masterDocumentDetails
                    ? undefined
                    : props.masterDocumentDetails[prop];

            const addDocIds = new Set(
                props.documentDetailsList.find((d) => d.id == masterFileId)
                    ?.additionalVersionIds ?? []
            );

            const docDetails = props.documentDetailsList.find(
                (d) =>
                    addDocIds.has(d.id) &&
                    d.masterDocumentId == masterFileId &&
                    d.contentType == contentType &&
                    d.contentSubtype == subtype
            );

            return !docDetails ? undefined : docDetails[prop];
        };

        return (
            <div className="seller-engagement-document-form">
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        documentSource: masterDocumentDetails.source,
                        documentSection: masterDocumentDetails.sectionId,
                        masterDocumentName: masterDocumentDetails.name ?? '',
                        staffBlindedDocumentName:
                            (getInitialDocumentPropValue(
                                masterDocumentDetails.id,
                                masterDocumentDetails.contentType,
                                DocumentDetailsContentSubtype.StaffBlinded,
                                'name'
                            ) as string) ?? '',
                        dmvsBlindedDocumentName:
                            (getInitialDocumentPropValue(
                                masterDocumentDetails.id,
                                masterDocumentDetails.contentType,
                                DocumentDetailsContentSubtype.DvmsBlinded,
                                'name'
                            ) as string) ?? '',
                        visibleDocumentName:
                            (getInitialDocumentPropValue(
                                masterDocumentDetails.id,
                                masterDocumentDetails.contentType,
                                DocumentDetailsContentSubtype.Visible,
                                'name'
                            ) as string) ?? '',
                        actionType: masterDocumentDetails.type,
                        contentType: masterDocumentDetails.contentType,
                        status: masterDocumentDetails.status,
                        documentTemplate:
                            masterDocumentDetails.templateId ?? -1,
                        questionnaireId:
                            masterDocumentDetails.questionnaireId ?? -1,
                        masterFileName: masterDocumentDetails.fileName ?? '',
                        staffBlindedFileName:
                            (getInitialDocumentPropValue(
                                masterDocumentDetails.id,
                                masterDocumentDetails.contentType,
                                DocumentDetailsContentSubtype.StaffBlinded,
                                'fileName'
                            ) as string) ?? '',
                        dmvsBlindedFileName:
                            (getInitialDocumentPropValue(
                                masterDocumentDetails.id,
                                masterDocumentDetails.contentType,
                                DocumentDetailsContentSubtype.DvmsBlinded,
                                'fileName'
                            ) as string) ?? '',
                        visibleFileName:
                            (getInitialDocumentPropValue(
                                masterDocumentDetails.id,
                                masterDocumentDetails.contentType,
                                DocumentDetailsContentSubtype.Visible,
                                'fileName'
                            ) as string) ?? '',
                        masterSourceFile: masterDocumentDetails.fileName
                            ? masterDocumentDetails.fileName?.replace(
                                  /(_\d+)(\.[^.]+)$/,
                                  '$2'
                              )
                            : '',
                        staffBlindedSourceFile:
                            (
                                getInitialDocumentPropValue(
                                    masterDocumentDetails.id,
                                    masterDocumentDetails.contentType,
                                    DocumentDetailsContentSubtype.StaffBlinded,
                                    'fileName'
                                ) as string
                            )?.replace(/(_\d+)(\.[^.]+)$/, '$2') ?? '',
                        dmvsBlindedSourceFile:
                            (
                                getInitialDocumentPropValue(
                                    masterDocumentDetails.id,
                                    masterDocumentDetails.contentType,
                                    DocumentDetailsContentSubtype.DvmsBlinded,
                                    'fileName'
                                ) as string
                            )?.replace(/(_\d+)(\.[^.]+)$/, '$2') ?? '',
                        visibleSourceFile:
                            (
                                getInitialDocumentPropValue(
                                    masterDocumentDetails.id,
                                    masterDocumentDetails.contentType,
                                    DocumentDetailsContentSubtype.Visible,
                                    'fileName'
                                ) as string
                            )?.replace(/(_\d+)(\.[^.]+)$/, '$2') ?? '',
                        linkedProjectId:
                            masterDocumentDetails.linkedProjectId ?? '',
                    }}
                    validationSchema={yup.object().shape({
                        actionType: yup
                            .number()
                            .min(0, 'Action type is required')
                            .uniqueGroupValidation(
                                props.timelineEvents ?? [],
                                props.currentEvent
                            ),
                        documentSource: yup
                            .number()
                            .min(0, 'Document source is required')
                            .notOneOf([4], 'Document source is required'),
                        documentSection: yup
                            .string()
                            .required('Document section is required')
                            .notOneOf(['-1'], 'Document section is required'),
                        documentTemplate: yup.number().when('documentSource', {
                            is: (val: EngagementDocumentSource) =>
                                Number(val) ===
                                EngagementDocumentSource.Template,
                            then: () =>
                                yup.number().min(1, 'Template is required'),
                        }),
                        questionnaireId: yup.number().when('documentSource', {
                            is: (val: EngagementDocumentSource) =>
                                Number(val) ===
                                EngagementDocumentSource.Questionnaire,
                            then: () =>
                                yup
                                    .number()
                                    .min(1, 'Questionnaire is required'),
                        }),
                        linkedProjectId: yup.string().when('documentSource', {
                            is: (val: EngagementDocumentSource) =>
                                Number(val) ===
                                EngagementDocumentSource.Questionnaire,
                            then: () =>
                                yup.string().required('Project is required'),
                        }),
                        contentType: yup.number().when('actionType', {
                            is: (val: ActionDocumentType) =>
                                Number(val) ===
                                ActionDocumentType.DocumentWithFeedback,
                            then: () => yup.number().min(0, 'Type is required'),
                        }),
                        masterDocumentName: yup
                            .string()
                            .trim()
                            .required('Document name is required'),
                        staffBlindedDocumentName: yup
                            .string()
                            .trim()
                            .when(
                                ['actionType', 'contentType', 'documentSource'],
                                {
                                    is: (
                                        actionType: number,
                                        contentType: number,
                                        documentSource: number
                                    ) =>
                                        actionType ===
                                            ActionDocumentType.DocumentWithFeedback &&
                                        contentType !==
                                            DocumentDetailsContentType.General &&
                                        documentSource ==
                                            EngagementDocumentSource.AdminUpload,
                                    then: (schema) =>
                                        schema.required(
                                            'Document name is required'
                                        ),
                                }
                            ),
                        dmvsBlindedDocumentName: yup
                            .string()
                            .trim()
                            .when(
                                ['actionType', 'contentType', 'documentSource'],
                                {
                                    is: (
                                        actionType: number,
                                        contentType: number,
                                        documentSource: number
                                    ) =>
                                        actionType ===
                                            ActionDocumentType.DocumentWithFeedback &&
                                        contentType !==
                                            DocumentDetailsContentType.General &&
                                        documentSource ==
                                            EngagementDocumentSource.AdminUpload,
                                    then: (schema) =>
                                        schema.required(
                                            'Document name is required'
                                        ),
                                }
                            ),
                        visibleDocumentName: yup
                            .string()
                            .trim()
                            .when(
                                ['actionType', 'contentType', 'documentSource'],
                                {
                                    is: (
                                        actionType: number,
                                        contentType: number,
                                        documentSource: number
                                    ) =>
                                        actionType ===
                                            ActionDocumentType.DocumentWithFeedback &&
                                        contentType !==
                                            DocumentDetailsContentType.General &&
                                        documentSource ==
                                            EngagementDocumentSource.AdminUpload,
                                    then: (schema) =>
                                        schema.required(
                                            'Document name is required'
                                        ),
                                }
                            ),
                    })}
                    onSubmit={async (values) => {
                        const multiFileNewAction =
                            values.actionType ==
                                ActionDocumentType.DocumentWithFeedback &&
                            values.contentType !=
                                DocumentDetailsContentType.General &&
                            values.documentSource ==
                                EngagementDocumentSource.AdminUpload;

                        const multiFileOldAction =
                            !!props.linkedAction &&
                            !!props.masterDocumentDetails &&
                            props.masterDocumentDetails?.type ==
                                ActionDocumentType.DocumentWithFeedback &&
                            props.masterDocumentDetails?.contentType !=
                                DocumentDetailsContentType.General &&
                            (props.masterDocumentDetails?.source ==
                                EngagementDocumentSource.AdminUpload ||
                                props.masterDocumentDetails?.source ==
                                    EngagementDocumentSource.BrokerUpload);

                        const notMultiFileActionAnymore =
                            multiFileOldAction && !multiFileNewAction;

                        const nowMultiFileAction =
                            !multiFileOldAction && multiFileNewAction;

                        let masterDocDetails =
                            isMasterFileUpdated &&
                            values.actionType != ActionDocumentType.ForSign &&
                            (values.contentType !=
                                DocumentDetailsContentType.PipOrPiq ||
                                values.documentSource !=
                                    EngagementDocumentSource.Questionnaire)
                                ? await uploadFile()
                                : masterDocumentDetails;

                        let newAdditionalDocDetails = [] as DocumentDetails[];
                        if (multiFileNewAction) {
                            const updatedFiles = selectedAdditionalFiles.filter(
                                (af) => af.isUpdated
                            );
                            if (updatedFiles.length > 0) {
                                props.setIsFileUploading(true);
                                const uploadedFilesDocDetails =
                                    await Promise.all(
                                        updatedFiles.map((f, index) =>
                                            new Promise((resolve) =>
                                                setTimeout(resolve, index * 100)
                                            ).then(() =>
                                                uploadFile(false, f.subType)
                                            )
                                        )
                                    );
                                props.setIsFileUploading(false);

                                newAdditionalDocDetails =
                                    additionalDocumentDetails.map((add) => {
                                        const updatedDetail =
                                            uploadedFilesDocDetails.find(
                                                (ud) => ud.id == add.id
                                            );
                                        return updatedDetail ?? add;
                                    });
                            } else {
                                newAdditionalDocDetails =
                                    additionalDocumentDetails;
                            }
                        }

                        if (newAdditionalDocDetails.length > 0) {
                            newAdditionalDocDetails =
                                newAdditionalDocDetails.map((ad) => {
                                    const fieldNamePrefix =
                                        DocumentDetailsContentSubtypeOptions.find(
                                            (o) => o.id == ad.contentSubtype
                                        )?.fieldName;
                                    const fieldName = `${fieldNamePrefix}DocumentName`;
                                    const docName = values[
                                        fieldName as keyof typeof values
                                    ] as string;
                                    return {
                                        ...ad,
                                        name: docName,
                                        sectionId: values.documentSection,
                                        status: values.status,
                                        additionalVersionIds: [] as string[],
                                        source: EngagementDocumentSource.AdminUpload,
                                        type: ActionDocumentType.DocumentWithFeedback,
                                        contentType: values.contentType,
                                    };
                                });
                        }

                        masterDocDetails = {
                            ...masterDocDetails,
                            name: values.masterDocumentName.trim(),
                            sectionId: values.documentSection,
                            type: values.actionType,
                            contentType: values.contentType,
                            contentSubtype:
                                values.contentType !=
                                DocumentDetailsContentType.General
                                    ? DocumentDetailsContentSubtype.Blinded
                                    : undefined,
                            status: values.status,
                            counterParty: TimelineParty.Seller,
                            source: Number(values.documentSource),
                            templateId: values.documentTemplate,
                            linkedProjectId: values.linkedProjectId,
                            questionnaireId: values.questionnaireId,
                            additionalVersionIds:
                                !multiFileNewAction || notMultiFileActionAnymore
                                    ? []
                                    : newAdditionalDocDetails.map(
                                          (ad) => ad.id
                                      ),
                        };
                        if (
                            masterDocumentDetails.type ==
                                ActionDocumentType.DocumentWithFeedback &&
                            values.actionType !=
                                ActionDocumentType.DocumentWithFeedback
                        ) {
                            const indexOfEventWithRefToThisAction =
                                props.timelineEvents.findIndex(
                                    (e) =>
                                        e.linkedAction?.actionReferenceId ===
                                        props.linkedAction?.id
                                );
                            if (indexOfEventWithRefToThisAction > -1) {
                                createNotification(
                                    `You can't change the Engagement document type because it is used in the event with index ${
                                        indexOfEventWithRefToThisAction + 1
                                    }.`,
                                    'error'
                                );
                                return;
                            }
                        }

                        let newAction: TimelineAction = null;
                        if (!!props.linkedAction) {
                            let oldRefAddIds =
                                nowMultiFileAction || notMultiFileActionAnymore
                                    ? undefined
                                    : props.linkedAction
                                          .oldReferenceItemAdditionalVersionIds;

                            if (oldAdditionalDocIds.some((o) => !!o.id)) {
                                if (
                                    !oldRefAddIds ||
                                    !isValidAdditionalOldRefIdsField(
                                        oldRefAddIds
                                    )
                                ) {
                                    oldRefAddIds =
                                        defaultAdditionalOldRefIdsField();
                                }

                                oldAdditionalDocIds.forEach((o) => {
                                    if (!o.id) return;
                                    let newSubTypeOldIds =
                                        oldRefAddIds[
                                            subTypeEnumToStringMap[o.subType]
                                        ];
                                    newSubTypeOldIds = [
                                        ...(newSubTypeOldIds ?? []),
                                        o.id,
                                    ];
                                    oldRefAddIds = {
                                        ...oldRefAddIds,
                                        [subTypeEnumToStringMap[o.subType]]: [
                                            ...newSubTypeOldIds,
                                        ],
                                    };
                                });
                            }

                            let updatedOldReferenceItemIds =
                                !!oldMasterDocumentId
                                    ? [
                                          ...(props.linkedAction
                                              .oldReferenceItemIds ?? []),
                                          oldMasterDocumentId,
                                      ]
                                    : props.linkedAction.oldReferenceItemIds;

                            newAction = {
                                ...props.linkedAction,
                                oldReferenceItemIds: updatedOldReferenceItemIds,
                                oldReferenceItemAdditionalVersionIds:
                                    oldRefAddIds,
                                actionType: Number(
                                    TimelineActionType.SellerEngagementDocument
                                ),
                                referenceItemId: masterDocDetails.id,
                            };
                        } else {
                            newAction = {
                                ...defaultTimelineAction,
                                actionType: Number(
                                    TimelineActionType.SellerEngagementDocument
                                ),
                                referenceItemId: masterDocDetails.id,
                            };
                        }

                        props.onSubmit(
                            newAction,
                            masterDocDetails,
                            newAdditionalDocDetails
                        );
                    }}>
                    {({ values, setFieldValue }) => {
                        return (
                            <div className="action-expanded-sub-form">
                                <Label for="actionType" className="mt-0">
                                    Action Type
                                </Label>
                                <Input
                                    type="select"
                                    name="actionType"
                                    disabled={props.disabled}
                                    onChange={(e) => {
                                        const newActionType = Number(
                                            e.target.value
                                        );
                                        const currentActionType = Number(
                                            values.actionType
                                        );

                                        const actionStatus =
                                            newActionType ===
                                                ActionDocumentType.Downloadable ||
                                            newActionType ===
                                                ActionDocumentType.Readonly
                                                ? DocumentDetailsStatus.Viewable
                                                : DocumentDetailsStatus.RequiresAction;

                                        setFieldValue(
                                            'status',
                                            actionStatus,
                                            true
                                        );
                                        if (
                                            !EngagementDocumentTypeOptionsMapping.find(
                                                (o) => o.value === newActionType
                                            )
                                                ?.fileExtensions?.split(',')
                                                .some((ext) =>
                                                    values.masterFileName.endsWith(
                                                        ext.trim()
                                                    )
                                                )
                                        ) {
                                            setFieldValue(
                                                'masterFileName',
                                                '',
                                                true
                                            );
                                            setSelectedMasterFile(null);
                                            setIsMasterFileUpdated(false);
                                            setSelectedAdditionalFiles(
                                                selectedAdditionalFiles.map(
                                                    (adf) => {
                                                        return {
                                                            ...adf,
                                                            file: null as File,
                                                            isUpdated: false,
                                                        };
                                                    }
                                                )
                                            );
                                        }
                                        if (
                                            EngagementDocumentSourceOptionsMapping.some(
                                                (o) =>
                                                    o.documentTypes.includes(
                                                        newActionType
                                                    ) &&
                                                    !o.documentTypes.includes(
                                                        currentActionType
                                                    )
                                            )
                                        ) {
                                            setFieldValue(
                                                'documentSource',
                                                -1,
                                                true
                                            );
                                            setFieldValue(
                                                'documentTemplate',
                                                0,
                                                true
                                            );
                                            setFieldValue(
                                                'masterFileName',
                                                '',
                                                true
                                            );
                                            setSelectedMasterFile(null);
                                            setIsMasterFileUpdated(false);
                                            setSelectedAdditionalFiles(
                                                selectedAdditionalFiles.map(
                                                    (adf) => {
                                                        return {
                                                            ...adf,
                                                            file: null as File,
                                                            isUpdated: false,
                                                        };
                                                    }
                                                )
                                            );
                                            setMasterDocumentDetails(
                                                props.masterDocumentDetails ??
                                                    createDefaultDocumentDetails(
                                                        true,
                                                        null,
                                                        null,
                                                        null,
                                                        null,
                                                        TimelineParty.Seller
                                                    )
                                            );
                                            initializeAdditionalDocuments(
                                                values.contentType
                                            );

                                            setOldAdditionalDocIds(
                                                getDefaultAdditionalOldDocIds()
                                            );
                                        }
                                    }}>
                                    <option value={-1}>Select</option>
                                    {EngagementDocumentTypeOptionsMapping.map(
                                        (option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}>
                                                {option.label}
                                            </option>
                                        )
                                    )}
                                </Input>
                                <Feedback name="actionType" />

                                {(() => {
                                    switch (Number(values.actionType)) {
                                        case ActionDocumentType.DocumentWithFeedback:
                                            return (
                                                <>
                                                    <Label for="contentType">
                                                        Document Type
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name="contentType"
                                                        disabled={
                                                            props.disabled
                                                        }
                                                        onChange={(e) => {
                                                            setFieldValue(
                                                                'documentSource',
                                                                -1
                                                            );

                                                            const newType =
                                                                Number(
                                                                    e.target
                                                                        .value
                                                                );
                                                            const currentType =
                                                                Number(
                                                                    values.contentType
                                                                );

                                                            if (
                                                                currentType !=
                                                                    DocumentDetailsContentType.General &&
                                                                newType ==
                                                                    DocumentDetailsContentType.General
                                                            ) {
                                                                const newAddDocDetails =
                                                                    getDefaultAdditionalDocDetails();
                                                                setAdditionalDocumentDetails(
                                                                    newAddDocDetails
                                                                );
                                                                setMasterDocumentDetails(
                                                                    {
                                                                        ...masterDocumentDetails,
                                                                        additionalVersionIds:
                                                                            newAddDocDetails.map(
                                                                                (
                                                                                    d
                                                                                ) =>
                                                                                    d.id
                                                                            ),
                                                                    }
                                                                );
                                                                setOldAdditionalDocIds(
                                                                    getDefaultAdditionalOldDocIds()
                                                                );
                                                            } else if (
                                                                currentType ==
                                                                    DocumentDetailsContentType.General &&
                                                                newType !=
                                                                    DocumentDetailsContentType.General
                                                            ) {
                                                                const newAddDocDetails =
                                                                    getDefaultAdditionalDocDetails(
                                                                        newType
                                                                    );
                                                                setAdditionalDocumentDetails(
                                                                    newAddDocDetails
                                                                );
                                                                setMasterDocumentDetails(
                                                                    {
                                                                        ...masterDocumentDetails,
                                                                        additionalVersionIds:
                                                                            newAddDocDetails.map(
                                                                                (
                                                                                    d
                                                                                ) =>
                                                                                    d.id
                                                                            ),
                                                                    }
                                                                );
                                                                setOldAdditionalDocIds(
                                                                    getDefaultAdditionalOldDocIds()
                                                                );
                                                            } else if (
                                                                currentType !=
                                                                    DocumentDetailsContentType.General &&
                                                                newType !=
                                                                    DocumentDetailsContentType.General &&
                                                                currentType !=
                                                                    newType
                                                            ) {
                                                                const newAddDocDetails =
                                                                    switchAdditionalDetailsContentType(
                                                                        newType
                                                                    );
                                                                setAdditionalDocumentDetails(
                                                                    newAddDocDetails
                                                                );
                                                                setMasterDocumentDetails(
                                                                    {
                                                                        ...masterDocumentDetails,
                                                                        contentType:
                                                                            newType,
                                                                        additionalVersionIds:
                                                                            newAddDocDetails.map(
                                                                                (
                                                                                    d
                                                                                ) =>
                                                                                    d.id
                                                                            ),
                                                                    }
                                                                );
                                                            }
                                                        }}>
                                                        {DocumentDetailsContentTypeOptions.map(
                                                            (option) => (
                                                                <option
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.name
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    <Feedback name="contentType" />
                                                </>
                                            );

                                        default:
                                            return null;
                                    }
                                })()}

                                <Label for="documentSource">
                                    Document Source
                                </Label>
                                <Input
                                    type="select"
                                    name="documentSource"
                                    disabled={props.disabled}
                                    onChange={(e) => {
                                        const newSource = Number(
                                            e.target.value
                                        );
                                        const currentSource = Number(
                                            values.documentSource
                                        );

                                        if (
                                            currentSource !=
                                                EngagementDocumentSource.Questionnaire &&
                                            newSource ==
                                                EngagementDocumentSource.Questionnaire
                                        ) {
                                            setAdditionalDocumentDetails([]);
                                            setMasterDocumentDetails({
                                                ...masterDocumentDetails,
                                                additionalVersionIds: [],
                                                contentSubtype: undefined,
                                            });
                                            setOldAdditionalDocIds([]);
                                        } else if (
                                            currentSource ==
                                                EngagementDocumentSource.Questionnaire &&
                                            newSource !=
                                                EngagementDocumentSource.Questionnaire
                                        ) {
                                            setMasterDocumentDetails(
                                                !!props.masterDocumentDetails
                                                    ? {
                                                          ...props.masterDocumentDetails,
                                                          linkedProjectId:
                                                              undefined,
                                                          questionnaireId:
                                                              undefined,
                                                      }
                                                    : {
                                                          ...masterDocumentDetails,
                                                          linkedProjectId:
                                                              undefined,
                                                          questionnaireId:
                                                              undefined,
                                                          contentSubtype:
                                                              DocumentDetailsContentSubtype.Blinded,
                                                      }
                                            );

                                            initializeAdditionalDocuments(
                                                DocumentDetailsContentType.PipOrPiq
                                            );

                                            setOldAdditionalDocIds(
                                                getDefaultAdditionalOldDocIds()
                                            );

                                            setFieldValue(
                                                'questionnaireId',
                                                undefined
                                            );
                                            setFieldValue(
                                                'linkedProjectId',
                                                undefined
                                            );
                                        }
                                    }}>
                                    <option value={-1}>Select</option>
                                    {EngagementDocumentSourceOptionsMapping.filter(
                                        (o) =>
                                            o.documentTypes.includes(
                                                Number(values.actionType)
                                            ) ||
                                            (Number(values.contentType) ===
                                                DocumentDetailsContentType.PipOrPiq &&
                                                o.value ===
                                                    EngagementDocumentSource.Questionnaire)
                                    ).map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Input>
                                <Feedback name="documentSource" />

                                <MarketplaceDocumentSectionField
                                    documentsSections={props.documentsSections}
                                    disabled={props.disabled}
                                    setDocumentSectionList={
                                        props.setDocumentSectionList
                                    }
                                    documentDetailsList={
                                        props.documentDetailsList
                                    }
                                    type="sellers"
                                    sectionsModalMode={'built-in'}
                                />

                                {(() => {
                                    const adminUploadFileSelector = () => {
                                        return (
                                            <>
                                                <FileSelector
                                                    extension={
                                                        EngagementDocumentTypeOptionsMapping.find(
                                                            (o) =>
                                                                Number(
                                                                    values.actionType
                                                                ) === o.value
                                                        ).fileExtensions
                                                    }
                                                    disabled={props.disabled}
                                                    file={selectedMasterFile}
                                                    inputFieldName={
                                                        'masterSourceFile'
                                                    }
                                                    setFile={(file: File) => {
                                                        changeFileHandler(file);
                                                    }}
                                                    fileName={
                                                        values.masterFileName
                                                    }
                                                    downloadFile={
                                                        props.downloadFile
                                                    }
                                                    setIsDocumentsHistoryPopupVisible={
                                                        props.setIsDocumentsHistoryPopupVisible
                                                    }
                                                    oldDocumentDetailsList={getOldReferenceIdsList()}
                                                />
                                                <Feedback name="masterFileName" />
                                            </>
                                        );
                                    };
                                    switch (Number(values.documentSource)) {
                                        case EngagementDocumentSource.Template:
                                            return (
                                                <>
                                                    <Label for="documentTemplate">
                                                        Document Template
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name="documentTemplate"
                                                        disabled={
                                                            props.disabled
                                                        }>
                                                        <option value={0}>
                                                            Select
                                                        </option>
                                                        {documentTemplates.map(
                                                            (option) => (
                                                                <option
                                                                    key={`template-${option.id}`}
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.name
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    <Feedback name="documentTemplate" />
                                                    {masterDocumentDetails.status ===
                                                    DocumentDetailsStatus.Completed ? (
                                                        <label
                                                            className="download-link-button"
                                                            onClick={(e) => {
                                                                props.downloadFile();
                                                            }}>
                                                            Download Signed
                                                            Document
                                                        </label>
                                                    ) : (
                                                        []
                                                    )}
                                                </>
                                            );
                                        case EngagementDocumentSource.AdminUpload:
                                            return adminUploadFileSelector();
                                        case EngagementDocumentSource.BrokerUpload:
                                            return adminUploadFileSelector();
                                        case EngagementDocumentSource.Questionnaire:
                                            return (
                                                <>
                                                    <Label for="linkedProjectId">
                                                        Data Request Project
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name="linkedProjectId"
                                                        disabled={
                                                            props.disabled
                                                        }
                                                        onChange={(e) => {
                                                            setFieldValue(
                                                                'questionnaireId',
                                                                0,
                                                                true
                                                            );
                                                            fetchTemplateOptions(
                                                                e.target.value
                                                            );
                                                        }}>
                                                        <option value={0}>
                                                            Select
                                                        </option>
                                                        {projectsAvailableToSelect.map(
                                                            (option) => (
                                                                <option
                                                                    key={`template-${option.id}`}
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.projectName
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    <Feedback name="linkedProjectId" />

                                                    <Label for="questionnaireId">
                                                        Questionnaire
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name="questionnaireId"
                                                        disabled={
                                                            props.disabled
                                                        }>
                                                        <option value={0}>
                                                            Select
                                                        </option>
                                                        {projectQuestionnaires.map(
                                                            (option) => (
                                                                <option
                                                                    key={`template-${option.id}`}
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.name
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    <Feedback name="questionnaireId" />
                                                </>
                                            );
                                        default:
                                            return null;
                                    }
                                })()}

                                {(() => {
                                    const multiDoc =
                                        values.actionType ==
                                            ActionDocumentType.DocumentWithFeedback &&
                                        values.contentType !=
                                            DocumentDetailsContentType.General &&
                                        (values.documentSource ==
                                            EngagementDocumentSource.AdminUpload ||
                                            values.documentSource ==
                                                EngagementDocumentSource.BrokerUpload);

                                    const blindedLabel =
                                        DocumentDetailsContentSubtypeOptions.find(
                                            (o) =>
                                                o.id ==
                                                DocumentDetailsContentSubtype.Blinded
                                        ).label;

                                    return (
                                        <Field
                                            className={`field ${
                                                multiDoc ? 'small-margin' : ''
                                            }`}
                                            name="masterDocumentName"
                                            label={`${
                                                multiDoc
                                                    ? `${blindedLabel} Document Name`
                                                    : 'Document Name'
                                            }`}
                                            disabled={props.disabled}
                                        />
                                    );
                                })()}

                                {(() => {
                                    const feedback =
                                        values.actionType ==
                                        ActionDocumentType.DocumentWithFeedback;
                                    const notGeneral =
                                        values.contentType !=
                                        DocumentDetailsContentType.General;
                                    const adminUpload =
                                        values.documentSource ==
                                            EngagementDocumentSource.AdminUpload ||
                                        values.documentSource ==
                                            EngagementDocumentSource.BrokerUpload;

                                    if (feedback && notGeneral && adminUpload) {
                                        return (
                                            <>
                                                {DocumentDetailsContentSubtypeOptions.map(
                                                    (option, index) =>
                                                        getAdditionalFileFields(
                                                            values.actionType,
                                                            option,
                                                            index
                                                        )
                                                )}
                                            </>
                                        );
                                    }

                                    return <></>;
                                })()}
                            </div>
                        );
                    }}
                </Formik>
            </div>
        );
    }
);
